import { Button, Flex, Text, useDisclosure } from '@chakra-ui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import moment from 'moment'
import { CaretLeft, CaretRight } from 'phosphor-react'
import { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  ReviewResponsibleMedicalRecordForm,
  UpdateResponsibleMedicalRecordDTO
} from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/UpdateMedicalRecordDTO'
import { useGetResponsibleMedicalRecordForReviewQuery } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/presentation/hooks/useGetResponsibleMedicalRecordForReviewQuery'
import { useGetResponsibleMedicalRecordForViewQuery } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/presentation/hooks/useGetResponsibleMedicalRecordForViewQuery'
import { useReviewResponsibleMedicalRecordMutation } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/presentation/hooks/useReviewResponsibleMedicalRecordMutation'
import LoadingScreen from '@/ui/components/Status/Loading/LoadingScreen'

import { Step } from '../../../../ResponsibleMedicalRecordFill/components/Step'
import { StepDivider } from '../../../../ResponsibleMedicalRecordFill/components/StepDivider'
import { StepStatus } from '../../../../ResponsibleMedicalRecordFill/types/StepStatus'
import { MedicalRecordReviewType } from '../../../constants/MedicalRecordReviewType'
import { useMedicalRecordManagementContext } from '../../../contexts/MedicalRecordManagementContext'
import CompleteRevisionMedicalRecordModal from './CompleteRevisionMedicalRecordModal'
import { AllergiesReviewStep } from './steps/AllergiesReviewStep'
import { ComorbiditiesReviewStep } from './steps/ComorbiditiesReviewStep'
import { FoodReviewStep } from './steps/FoodReviewStep'
import { MedicationReviewStep } from './steps/MedicationReviewStep'
import { PatientDataReviewStep } from './steps/PatientDataReviewStep'

function getStepState(step: number, stepNumber: number): StepStatus {
  const currentNumber = stepNumber ?? -1
  return step < currentNumber
    ? 'COMPLETE'
    : step === currentNumber
      ? 'CURRENT'
      : 'FUTURE'
}

export default function MedicalRecordManagementRevision({
  mode
}: {
  mode: MedicalRecordReviewType
}) {
  const isViewMode = mode === 'VIEW'

  const { t } = useTranslation()
  const { isOpen, onClose, onOpen } = useDisclosure()
  const {
    selectedItemId: id,
    onReviewViewClose,
    patient
  } = useMedicalRecordManagementContext()
  const formRef = useRef<HTMLFormElement>(null)

  const [step, setStep] = useState(1)

  const form = useForm<ReviewResponsibleMedicalRecordForm>({
    resolver: zodResolver(ReviewResponsibleMedicalRecordForm),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      allergies: [],
      medications: [],
      foods: {
        foods: []
      },
      comorbidities: []
    }
  })

  const [
    responsibleName,
    responsibleDocument,
    responsibleBirthday,
    responsibleGender,
    responsibleType
  ] = form.watch([
    'responsibleName',
    'responsibleDocument',
    'responsibleBirthday',
    'responsibleGender',
    'responsibleType'
  ])

  const responsible = {
    responsibleName,
    responsibleDocument,
    responsibleBirthday,
    responsibleGender,
    responsibleType
  } as const

  async function triggerFormStep(step: number): Promise<boolean> {
    switch (step) {
      case 1:
        return await form.trigger([
          'height',
          'weight',
          'patientBirthDay',
          'bloodType',
          'patientGender'
        ])
      case 2:
        return await form.trigger(['allergies'])
      case 3:
        return await form.trigger(['medications'])
      case 4:
        return await form.trigger(['foods'])
      case 5:
        return await form.trigger(['comorbidities'])
      default:
        return true
    }
  }

  const getResponsibleMedicalRecordForReviewQuery =
    useGetResponsibleMedicalRecordForReviewQuery({ id, enabled: !isViewMode })

  const getResponsibleMedicalRecordForViewQuery =
    useGetResponsibleMedicalRecordForViewQuery({ id, enabled: isViewMode })

  useEffect(() => {
    if (!isViewMode && getResponsibleMedicalRecordForReviewQuery.data) {
      if (
        getResponsibleMedicalRecordForReviewQuery.data.sendResponsible
          .responsibleName
      )
        form.setValue(
          'responsibleName',
          getResponsibleMedicalRecordForReviewQuery.data.sendResponsible
            .responsibleName
        )
      if (
        getResponsibleMedicalRecordForReviewQuery.data.sendResponsible
          .responsibleDocument
      )
        form.setValue(
          'responsibleDocument',
          getResponsibleMedicalRecordForReviewQuery.data.sendResponsible
            .responsibleDocument
        )
      if (
        getResponsibleMedicalRecordForReviewQuery.data.sendResponsible
          .responsibleBirthday
      )
        form.setValue(
          'responsibleBirthday',
          getResponsibleMedicalRecordForReviewQuery.data.sendResponsible
            .responsibleBirthday
        )
      if (
        getResponsibleMedicalRecordForReviewQuery.data.sendResponsible
          .responsibleGender
      )
        form.setValue(
          'responsibleGender',
          getResponsibleMedicalRecordForReviewQuery.data.sendResponsible
            .responsibleGender
        )
      if (
        getResponsibleMedicalRecordForReviewQuery.data.sendResponsible
          .responsibleType
      )
        form.setValue(
          'responsibleType',
          getResponsibleMedicalRecordForReviewQuery.data.sendResponsible
            .responsibleType
        )
    }

    if (isViewMode && getResponsibleMedicalRecordForViewQuery.data) {
      Array.from(
        Object.entries(
          getResponsibleMedicalRecordForViewQuery?.data.registerOperational ??
            {}
        )
      ).forEach(([key, value]) => {
        // @ts-expect-error key is a valid form field
        form.setValue(key, value)
      })
    }
  }, [
    getResponsibleMedicalRecordForReviewQuery.data,
    getResponsibleMedicalRecordForViewQuery.data,
    isViewMode
  ])

  useEffect(() => {
    if (
      id ||
      getResponsibleMedicalRecordForReviewQuery.isError ||
      (!getResponsibleMedicalRecordForReviewQuery.isLoading &&
        !getResponsibleMedicalRecordForReviewQuery.data)
    )
      return
    onReviewViewClose()
  }, [id, getResponsibleMedicalRecordForReviewQuery.data])

  const reviewResponsibleMedicalRecordMutation =
    useReviewResponsibleMedicalRecordMutation({
      onSuccess() {
        onOpen()
      }
    })

  if (getResponsibleMedicalRecordForReviewQuery.isFetching) {
    return <LoadingScreen />
  }

  return (
    <>
      <FormProvider {...form}>
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
          onSubmit={form.handleSubmit(
            (data) => {
              const d = UpdateResponsibleMedicalRecordDTO.safeParse(data)
              if (!d.success) return console.error(d.error)
              reviewResponsibleMedicalRecordMutation.mutate({
                data: d.data,
                patientId:
                  getResponsibleMedicalRecordForReviewQuery.data
                    ?.registerPatient.patient.userId ?? '*',
                responsibleMedicalRecordId: id
              })
            },
            (err) => console.error(err)
          )}
          ref={formRef}
        >
          <Flex flexDir={'column'} gap={'6'} p={'6'} h={'full'}>
            <Flex gap={'4'}>
              <Flex>
                <Button
                  variant={'outline'}
                  colorScheme={'blue'}
                  borderColor={'custom.blue.darker'}
                  color={'custom.blue.darker'}
                  p={'2'}
                  gap={'3'}
                  fontSize={'1.125rem'}
                  fontWeight={'500'}
                  onClick={onReviewViewClose}
                >
                  <CaretLeft /> Voltar
                </Button>
              </Flex>
              <Flex
                gap={'8'}
                border={'solid 1px'}
                borderColor={'custom.gray.600'}
                borderRadius={'0.25rem'}
                px={'4'}
                py={'2'}
                flex={'1'}
              >
                <Flex flexDir={'column'} gap={'1'}>
                  <Text
                    fontSize={'0.875rem'}
                    fontWeight={'400'}
                    color={'custom.gray.400'}
                  >
                    Nome do responsável
                  </Text>
                  <Text
                    fontSize={'1rem'}
                    fontWeight={'500'}
                    color={'custom.gray.200'}
                  >
                    {responsible.responsibleName || '-'}
                  </Text>
                </Flex>
                <Flex flexDir={'column'} gap={'1'}>
                  <Text
                    fontSize={'0.875rem'}
                    fontWeight={'400'}
                    color={'custom.gray.400'}
                  >
                    Data de nascimento
                  </Text>
                  <Text
                    fontSize={'1rem'}
                    fontWeight={'500'}
                    color={'custom.gray.200'}
                  >
                    {responsible.responsibleBirthday
                      ? moment(responsible.responsibleBirthday).format('L')
                      : '-'}
                  </Text>
                </Flex>
                <Flex flexDir={'column'} gap={'1'}>
                  <Text
                    fontSize={'0.875rem'}
                    fontWeight={'400'}
                    color={'custom.gray.400'}
                  >
                    Documento
                  </Text>
                  <Text
                    fontSize={'1rem'}
                    fontWeight={'500'}
                    color={'custom.gray.200'}
                  >
                    {responsible.responsibleDocument || '-'}
                  </Text>
                </Flex>
                <Flex flexDir={'column'} gap={'1'}>
                  <Text
                    fontSize={'0.875rem'}
                    fontWeight={'400'}
                    color={'custom.gray.400'}
                  >
                    Parentesco
                  </Text>
                  <Text
                    fontSize={'1rem'}
                    fontWeight={'500'}
                    color={'custom.gray.200'}
                  >
                    {responsible.responsibleType
                      ? t(
                          `general.common.responsibles.${
                            responsible.responsibleType.toLowerCase() as Lowercase<
                              typeof responsible.responsibleType
                            >
                          }`
                        )
                      : '-'}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
            <Flex>
              <Step
                index={1}
                name="Dados do cliente"
                onClick={() =>
                  !reviewResponsibleMedicalRecordMutation.isPending &&
                  setStep(1)
                }
                status={getStepState(1, step)}
              />
              <StepDivider />
              <Step
                index={2}
                name="Alergias"
                onClick={() =>
                  !reviewResponsibleMedicalRecordMutation.isPending &&
                  setStep(2)
                }
                status={getStepState(2, step)}
              />
              <StepDivider />
              <Step
                index={3}
                name="Medicamentos usados em casa"
                onClick={() =>
                  !reviewResponsibleMedicalRecordMutation.isPending &&
                  setStep(3)
                }
                status={getStepState(3, step)}
              />
              <StepDivider />
              <Step
                index={4}
                name="Alimentação"
                onClick={() =>
                  !reviewResponsibleMedicalRecordMutation.isPending &&
                  setStep(4)
                }
                status={getStepState(4, step)}
              />
              <StepDivider />
              <Step
                index={5}
                name="Comorbidades"
                onClick={() =>
                  !reviewResponsibleMedicalRecordMutation.isPending &&
                  setStep(5)
                }
                status={getStepState(5, step)}
              />
            </Flex>
            <Flex justifyContent={'space-between'}>
              <Text
                fontSize={'1.125rem'}
                fontWeight={'500'}
                color={'custom.gray.100'}
              >
                Informações fornecidas pelo responsável
              </Text>
              <Text
                fontSize={'1.125rem'}
                fontWeight={'500'}
                color={'custom.gray.100'}
              >
                Informações ativas no sistema
              </Text>
            </Flex>
            <Flex flexDir={'column'} overflow={'auto'}>
              {isViewMode &&
                (getResponsibleMedicalRecordForViewQuery.isLoading ? (
                  <LoadingScreen />
                ) : (
                  getResponsibleMedicalRecordForViewQuery.data &&
                  (step === 1 ? (
                    <PatientDataReviewStep
                      data={getResponsibleMedicalRecordForViewQuery.data}
                      mode={'VIEW'}
                    />
                  ) : step === 2 ? (
                    <AllergiesReviewStep
                      data={getResponsibleMedicalRecordForViewQuery.data}
                      mode={'VIEW'}
                    />
                  ) : step === 3 ? (
                    <MedicationReviewStep
                      data={getResponsibleMedicalRecordForViewQuery.data}
                      mode={'VIEW'}
                    />
                  ) : step === 4 ? (
                    <FoodReviewStep
                      data={getResponsibleMedicalRecordForViewQuery.data}
                      mode={'VIEW'}
                    />
                  ) : step === 5 ? (
                    <ComorbiditiesReviewStep
                      data={getResponsibleMedicalRecordForViewQuery.data}
                      mode={'VIEW'}
                      isLoading={
                        reviewResponsibleMedicalRecordMutation.isPending
                      }
                    />
                  ) : (
                    <></>
                  ))
                ))}
              {!isViewMode &&
                (getResponsibleMedicalRecordForReviewQuery.isLoading ? (
                  <LoadingScreen />
                ) : (
                  getResponsibleMedicalRecordForReviewQuery.data &&
                  (step === 1 ? (
                    <PatientDataReviewStep
                      data={getResponsibleMedicalRecordForReviewQuery.data}
                      mode={'REVIEW'}
                    />
                  ) : step === 2 ? (
                    <AllergiesReviewStep
                      data={getResponsibleMedicalRecordForReviewQuery.data}
                      mode={'REVIEW'}
                    />
                  ) : step === 3 ? (
                    <MedicationReviewStep
                      data={getResponsibleMedicalRecordForReviewQuery.data}
                      mode={'REVIEW'}
                    />
                  ) : step === 4 ? (
                    <FoodReviewStep
                      data={getResponsibleMedicalRecordForReviewQuery.data}
                      mode={'REVIEW'}
                    />
                  ) : step === 5 ? (
                    <ComorbiditiesReviewStep
                      data={getResponsibleMedicalRecordForReviewQuery.data}
                      mode={'REVIEW'}
                      isLoading={
                        reviewResponsibleMedicalRecordMutation.isPending
                      }
                    />
                  ) : (
                    <></>
                  ))
                ))}
            </Flex>
            {!(isViewMode && step === 5) && (
              <Flex justifyContent={'flex-end'}>
                <Button
                  colorScheme="blue"
                  bgColor={'custom.blue.darker'}
                  textAlign={'center'}
                  fontSize={'1rem'}
                  fontWeight={'500'}
                  px={'16'}
                  onClick={async (e) => {
                    if (!(await triggerFormStep(step))) return
                    if (step === 5)
                      formRef.current?.requestSubmit(e.currentTarget)
                    else setStep(step + 1)
                  }}
                  isLoading={reviewResponsibleMedicalRecordMutation.isPending}
                  isDisabled={
                    reviewResponsibleMedicalRecordMutation.isPending ||
                    (isViewMode &&
                      !getResponsibleMedicalRecordForViewQuery.data) ||
                    (!isViewMode &&
                      !getResponsibleMedicalRecordForReviewQuery.data)
                  }
                >
                  {step === 5 ? 'Salvar alterações' : 'Próxima etapa'}{' '}
                  <CaretRight />
                </Button>
              </Flex>
            )}
          </Flex>
        </form>
      </FormProvider>
      {isOpen && (
        <CompleteRevisionMedicalRecordModal
          onClose={() => {
            onClose()
            onReviewViewClose()
          }}
          patientId={patient.userId}
          patientName={patient.avatarName ?? patient.username}
        />
      )}
    </>
  )
}
